import React, { useState } from 'react';
import { Card, Form, Input, Button, Avatar, Upload, Typography, Divider, message, Space, App, Tabs } from 'antd';
import { UserOutlined, UploadOutlined, EditOutlined, SaveOutlined, IdcardOutlined, InfoCircleOutlined, LockOutlined } from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import apiService from '../services/apiService';

const { Title, Text } = Typography;

const ProfilePage = () => {
  const { user, updateProfile, changePassword, isLoading } = useAuthStore();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [editMode, setEditMode] = useState(false);
  console.log(user,"user");
  
  
  // Use message API properly
  const [messageApi, contextHolder] = message.useMessage();

  const handleProfileUpdate = async (values) => {
    const result = await updateProfile(values);
    if (result.success) {
      messageApi.success('Profile updated successfully');
      setEditMode(false);
    } else {
      messageApi.error(result.error || 'Failed to update profile');
    }
  };

  const handlePasswordChange = async (values) => {
    const result = await changePassword(values);
    if (result.success) {
      messageApi.success('Password changed successfully');
      passwordForm.resetFields();
    } else {
      messageApi.error(result.error || 'Failed to change password');
    }
  };

  const handleAvatarUpload = async (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    
    if (info.file.status === 'done') {
      messageApi.success('Avatar updated successfully');
      // Refresh user data to get updated avatar
      const authStore = useAuthStore.getState();
      await authStore.initializeAuth();
    } else if (info.file.status === 'error') {
      messageApi.error('Failed to upload avatar');
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {contextHolder}
      {/* Header */}
      <div>
        <Title level={2} className="mb-2">Profile</Title>
        <Text className="text-gray-600 dark:text-gray-400">
          Manage your account settings and preferences
        </Text>
      </div>

      <Tabs 
        defaultActiveKey="profile" 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-soft p-6"
        items={[
          {
            key: 'profile',
            label: (
              <span>
                <IdcardOutlined />
                Profile
              </span>
            ),
            children: (
          <div className="flex items-start space-x-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center space-y-4">
              <Avatar 
                size={120} 
                icon={<UserOutlined />}
                src={user?.avatar ? `http://localhost:5002${user.avatar}` : null}
                className="border-4 border-gray-200 dark:border-gray-600"
              />
              <Upload
                name="avatar"
                showUploadList={false}
                customRequest={async ({ file, onSuccess, onError }) => {
                  const formData = new FormData();
                  formData.append('avatar', file);
                  
                  try {
                    const response = await apiService.post('/users/avatar', formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data',
                      },
                    });
                    onSuccess(response.data);
                  } catch (error) {
                    onError(error);
                  }
                }}
                onChange={handleAvatarUpload}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />} size="small">
                  Change Avatar
                </Button>
              </Upload>
            </div>

            {/* Profile Form */}
            <div className="flex-1">
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                  email: user?.email,
                  phone: user?.phone,
                }}
                onFinish={handleProfileUpdate}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[{ required: true, message: 'Please enter your first name' }]}
                  >
                    <Input 
                      disabled={!editMode}
                      className="focus-visible:focus"
                    />
                  </Form.Item>

                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[{ required: true, message: 'Please enter your last name' }]}
                  >
                    <Input 
                      disabled={!editMode}
                      className="focus-visible:focus"
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input 
                      disabled={!editMode}
                      className="focus-visible:focus"
                    />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label="Phone"
                  >
                    <Input 
                      disabled={!editMode}
                      className="focus-visible:focus"
                    />
                  </Form.Item>
                </div>

                <div className="flex justify-end space-x-2 mt-6">
                  {editMode ? (
                    <Space>
                      <Button onClick={() => setEditMode(false)}>
                        Cancel
                      </Button>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={isLoading}
                        icon={<SaveOutlined />}
                        className="focus-visible:focus"
                      >
                        Save Changes
                      </Button>
                    </Space>
                  ) : (
                    <Button 
                      type="primary" 
                      onClick={() => setEditMode(true)}
                      icon={<EditOutlined />}
                      className="focus-visible:focus"
                    >
                      Edit Profile
                    </Button>
                  )}
                </div>
              </Form>
            </div>
          </div>
            )
          },
          {
            key: 'account',
            label: (
              <span>
                <InfoCircleOutlined />
                Account
              </span>
            ),
            children: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Text className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Role
              </Text>
              <Text className="text-base capitalize">
                {user?.role?.replace('_', ' ')}
              </Text>
            </div>

            <div>
              <Text className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Member Since
              </Text>
              <Text className="text-base">
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
              </Text>
            </div>

            <div>
              <Text className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Last Login
              </Text>
              <Text className="text-base">
                {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'N/A'}
              </Text>
            </div>

            <div>
              <Text className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Email Verified
              </Text>
              <Text className={`text-base ${user?.emailVerified ? 'text-green-600' : 'text-red-600'}`}>
                {user?.emailVerified ? 'Verified' : 'Not Verified'}
              </Text>
            </div>
          </div>
            )
          },
          {
            key: 'security',
            label: (
              <span>
                <LockOutlined />
                Security
              </span>
            ),
            children: (
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handlePasswordChange}
            className="max-w-md"
          >
            <Form.Item
              name="currentPassword"
              label="Current Password"
              rules={[{ required: true, message: 'Please enter your current password' }]}
            >
              <Input.Password className="focus-visible:focus" />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[
                { required: true, message: 'Please enter a new password' },
                { min: 8, message: 'Password must be at least 8 characters' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                  message: 'Password must contain uppercase, lowercase, number and special character'
                }
              ]}
            >
              <Input.Password className="focus-visible:focus" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm New Password"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password className="focus-visible:focus" />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={isLoading}
                className="focus-visible:focus"
              >
                Change Password
              </Button>
            </Form.Item>
          </Form>
            )
          }
        ]}
      />
    </div>
  );
};

export default ProfilePage;
